// Schedule page rules:
// - No coaching logic
// - No analytics
// - No vertical scrolling
// - Week view is default
// - This page answers: "When am I training?"

import { useTranslation } from 'react-i18next';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { TrainingCalendar } from '@/components/calendar/TrainingCalendar';
import { GarminAttribution } from '@/components/activities/GarminAttribution';
import { AppleHealthAttribution } from '@/components/activities/AppleHealthAttribution';
import { useSyncTodayWorkout } from '@/hooks/useSyncTodayWorkout';
import { useAutoMatchSessions } from '@/hooks/useAutoMatchSessions';
import { PlanCoachChat } from '@/components/plan/PlanCoachChat';
import { getIntegrationsStatus } from '@/lib/api';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';

export default function Calendar() {
  useSyncTodayWorkout();
  useAutoMatchSessions(true);
  const { t } = useTranslation();
  const { data: integrationsStatus } = useAuthenticatedQuery({
    queryKey: ['integrations', 'status'],
    queryFn: getIntegrationsStatus,
    staleTime: 2 * 60 * 1000,
  });
  const garminConnected = integrationsStatus?.integrations?.some(
    (i: { provider: string; connected: boolean }) => i.provider === 'garmin' && i.connected
  ) ?? false;
  const appleHealthConnected = integrationsStatus?.integrations?.some(
    (i: { provider: string; connected: boolean }) => i.provider === 'apple_health' && i.connected
  ) ?? false;

  return (
    <AppLayout>
      <div className="flex flex-col h-full min-h-screen bg-transparent">
        <PageHeader
          title={t('nav.schedule')}
          subtitle={t('calendar.subtitle')}
          statusLabel={t('calendar.statusLabel')}
        />

        {/* Calendar - flexible container */}
        <div className="flex-1 min-h-0">
          <TrainingCalendar />
        </div>
        {garminConnected && (
          <div className="flex-shrink-0 mt-4 pt-4 border-t border-border/50">
            <GarminAttribution />
          </div>
        )}
        {appleHealthConnected && (
          <div className="flex-shrink-0 mt-4 pt-4 border-t border-border/50">
            <AppleHealthAttribution />
          </div>
        )}

        {/* Floating Coach Chat */}
        <PlanCoachChat />
      </div>
    </AppLayout>
  );
}
